import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { MarketplaceSeller } from '@/types/residents/marketplaceListing';
import { StarRating } from '../StarRating';
import { UserAvatar } from './UserAvatar';
import { SVG_COMPONENTS } from '@/assets/admin';

const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

interface SellerCardProps {
  seller: MarketplaceSeller;
  onPress?: () => void;
  showChevron?: boolean;
}

/**
 * Reusable seller card showing avatar, name, and rating
 */
export const SellerCard: React.FC<SellerCardProps> = ({
  seller,
  onPress,
  showChevron = true,
}) => {
  const mc = useMarketplaceColors();
  const space = useSpacing();

  const styles = useMemo(() => StyleSheet.create({
    // Web: the divider and the 20pt under it stay outside the hover plate,
    // which hugs the row itself and reaches space.sm past it.
    container: {
      paddingTop: 20,
      borderTopWidth: 1,
      borderTopColor: mc.dividerGray,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    info: { flex: 1, gap: 4 },
    nameRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    name: { fontSize: 14, fontWeight: '500', color: mc.textPrimary, lineHeight: 21, flex: 1 },
    ratingRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 4 },
    reviewCount: { fontSize: 12, fontWeight: '500', color: mc.textSecondary, lineHeight: 18 },
  }), [mc]);

  const content = (
    <>
      {/* Avatar */}
      <UserAvatar initials={seller.initials} size="large" />

      {/* Seller Info */}
      <View style={styles.info}>
        <View style={styles.nameRow}>
          <Text style={styles.name}>{seller.name}</Text>
          {showChevron && onPress && (
            <ChevronRightIcon width={20} height={20} color={mc.textSecondary} />
          )}
        </View>
        <View style={styles.ratingRow}>
          <StarRating rating={seller.rating} />
          <Text style={styles.reviewCount}>({seller.reviewCount})</Text>
        </View>
      </View>
    </>
  );

  // Phones keep the original single node: divider, padding and row in one
  // pressable, so the strip under the divider stays part of the tap target.
  if (Platform.OS !== 'web') {
    return onPress ? (
      <Hoverable hover="row" style={[styles.container, styles.row]} onPress={onPress}>
        {content}
      </Hoverable>
    ) : (
      <View style={[styles.container, styles.row]}>{content}</View>
    );
  }

  return (
    <View style={styles.container}>
      {onPress ? (
        <Hoverable hover="row" bleed={{ x: space.sm, y: space.sm }} style={styles.row} onPress={onPress}>
          {content}
        </Hoverable>
      ) : (
        <View style={styles.row}>{content}</View>
      )}
    </View>
  );
};
